import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { colors, s4, s32, s16, fade, s8, r4, shadowM, radius, zIndex } from "@ardoq/design-tokens";
import { SvgSpriteIcon, ShapeType, Icon, IconSize, IconName, ICON_SELECTOR, OpenInNewIcon, CloseIcon, ErrorIcon, WarningIcon, CheckCircleIcon, InfoIcon, SaveIcon, VisibilityIcon, ProductUpdatesIcon, AnimatedChevron, SpinnerIcon } from "@ardoq/icons";
import styled, { css } from "styled-components";
import { text2Bold, text2, normal14, semibold14, caption, Header4, Text, Header3, Paragraph } from "@ardoq/typography";
import { ComponentWithDropdown, DropdownOptionType } from "@ardoq/dropdown-menu";
import { forwardRef, useRef, useState, useEffect, createElement, Children, isValidElement } from "react";
import { FlexBox, Stack, Box } from "@ardoq/layout";
import { focusableCSS, hideScrollbars, getFocusRingSpreadCSS, Space, FOCUS_RING_SPREAD, focusRingCSS } from "@ardoq/style-helpers";
import { withPlainTextPopover, POPOVER_ID_ATTR } from "@ardoq/popovers";
import { useResizeObserver, useChevronSliders } from "@ardoq/hooks";
import { useFocusRing } from "@react-aria/focus";
import { useButton } from "@react-aria/button";
import { Checkbox } from "@ardoq/forms";
import { GhostButton, SecondaryButton, BUTTON_SELECTOR, ButtonSize, Button, ButtonType } from "@ardoq/button";
import { actionCreator, reducedStream, reducer, ofType, extractPayload, connect } from "@ardoq/rxbeach";
import { Subject, withLatestFrom, tap } from "rxjs";
import { logError } from "@ardoq/logging";
import { omit } from "lodash";
var StatusType = /* @__PURE__ */ ((StatusType2) => {
  StatusType2[StatusType2["DEFAULT"] = 1] = "DEFAULT";
  StatusType2[StatusType2["NEW"] = 2] = "NEW";
  StatusType2[StatusType2["ERROR"] = 3] = "ERROR";
  StatusType2[StatusType2["WARNING"] = 4] = "WARNING";
  StatusType2[StatusType2["SUCCESS"] = 5] = "SUCCESS";
  StatusType2[StatusType2["INFO"] = 6] = "INFO";
  StatusType2[StatusType2["SCENARIO"] = 7] = "SCENARIO";
  StatusType2[StatusType2["BETA"] = 8] = "BETA";
  StatusType2[StatusType2["TRIAL"] = 9] = "TRIAL";
  return StatusType2;
})(StatusType || {});
const badgeBorderColors = {
  [StatusType.DEFAULT]: colors.borderAction,
  [StatusType.SUCCESS]: colors.borderSuccess,
  [StatusType.WARNING]: colors.borderWarning,
  [StatusType.ERROR]: colors.borderError,
  [StatusType.NEW]: colors.borderBrand,
  [StatusType.INFO]: colors.borderInfo,
  [StatusType.SCENARIO]: colors.surfaceScenario,
  [StatusType.BETA]: colors.blue50,
  [StatusType.TRIAL]: colors.pink60
};
const badgeIconColors = {
  [StatusType.DEFAULT]: colors.icon,
  [StatusType.SUCCESS]: colors.iconSuccess,
  [StatusType.WARNING]: colors.iconWarning,
  [StatusType.ERROR]: colors.iconError,
  [StatusType.NEW]: colors.iconBrand,
  [StatusType.INFO]: colors.iconInfo,
  [StatusType.SCENARIO]: colors.white,
  [StatusType.BETA]: colors.white,
  [StatusType.TRIAL]: colors.white
};
const badgeDotFills = {
  [StatusType.DEFAULT]: colors.icon,
  [StatusType.SUCCESS]: colors.iconSuccess,
  [StatusType.WARNING]: colors.iconWarning,
  [StatusType.ERROR]: colors.iconError,
  [StatusType.NEW]: colors.iconBrand,
  [StatusType.INFO]: colors.blue80,
  [StatusType.SCENARIO]: colors.blue35,
  [StatusType.BETA]: colors.blue50,
  [StatusType.TRIAL]: colors.pink60
};
const badgeFills = {
  [StatusType.DEFAULT]: colors.surfaceStatusNeutral,
  [StatusType.SUCCESS]: colors.surfaceSuccess,
  [StatusType.WARNING]: colors.surfaceWarning,
  [StatusType.ERROR]: colors.surfaceError,
  [StatusType.NEW]: colors.surfaceBrand,
  [StatusType.INFO]: colors.surfaceInfo,
  [StatusType.SCENARIO]: colors.surfaceScenario,
  [StatusType.BETA]: colors.blue50,
  [StatusType.TRIAL]: colors.pink60
};
const badgeTextColors = {
  [StatusType.DEFAULT]: colors.textDefault,
  [StatusType.SUCCESS]: colors.textSuccess,
  [StatusType.WARNING]: colors.textWarning,
  [StatusType.ERROR]: colors.textError,
  [StatusType.NEW]: colors.textBrandLight,
  [StatusType.INFO]: colors.textInfo,
  [StatusType.SCENARIO]: colors.textOnInvertedStrong,
  [StatusType.BETA]: colors.textOnInvertedStrong,
  [StatusType.TRIAL]: colors.textOnInvertedStrong
};
const badgeHover = {
  [StatusType.BETA]: colors.blue35
};
const badgeActive = {
  [StatusType.BETA]: colors.blue25
};
const BadgeDot = ({
  size,
  color,
  style,
  ...rest
}) => {
  const badgeDotColor = badgeDotFills[color];
  const iconStyle = {
    ...style,
    height: size,
    width: size,
    minHeight: size,
    minWidth: size,
    color: badgeDotColor,
    fill: badgeDotColor
  };
  return /* @__PURE__ */ jsx(
    SvgSpriteIcon,
    {
      symbolName: ShapeType.CIRCLE,
      style: iconStyle,
      height: size,
      width: size,
      ...rest
    }
  );
};
var BadgeDotSize = /* @__PURE__ */ ((BadgeDotSize2) => {
  BadgeDotSize2[BadgeDotSize2["SMALL"] = 8] = "SMALL";
  BadgeDotSize2[BadgeDotSize2["LARGE"] = 16] = "LARGE";
  return BadgeDotSize2;
})(BadgeDotSize || {});
const Container$2 = styled.div`
  position: relative;
  display: inline-flex;
`;
const IconBadgeIcon = styled(Icon)`
  top: 0;
  left: 0;
  position: relative;
  width: ${({ $iconSize }) => $iconSize}px;
  height: ${({ $iconSize }) => $iconSize}px;
`;
const ICON_BADGE_ICON_SELECTOR = IconBadgeIcon.toString();
const StyledBadgeDot = styled(BadgeDot)`
  top: -3px;
  left: ${({ $iconSize }) => $iconSize - BadgeDotSize.SMALL / 2}px;
  position: absolute;
`;
const IconBadge = ({
  iconName,
  iconStyle,
  statusType,
  iconSize = IconSize.REGULAR,
  ...rest
}) => /* @__PURE__ */ jsxs(Container$2, { ...rest, children: [
  /* @__PURE__ */ jsx(
    IconBadgeIcon,
    {
      iconSize,
      iconName,
      style: iconStyle,
      $iconSize: iconSize
    }
  ),
  /* @__PURE__ */ jsx(
    StyledBadgeDot,
    {
      $iconSize: iconSize,
      color: statusType,
      size: BadgeDotSize.SMALL
    }
  )
] });
const DEFAULT_BADGE_COLOR_MAP = {
  default: {
    borderColor: badgeBorderColors[StatusType.DEFAULT],
    color: badgeTextColors[StatusType.DEFAULT],
    backgroundColor: badgeFills[StatusType.DEFAULT],
    iconColor: badgeIconColors[StatusType.DEFAULT]
  }
};
const SUCCESS_BADGE_COLOR_MAP = {
  default: {
    borderColor: badgeBorderColors[StatusType.SUCCESS],
    color: badgeTextColors[StatusType.SUCCESS],
    backgroundColor: badgeFills[StatusType.SUCCESS],
    iconColor: badgeIconColors[StatusType.SUCCESS]
  }
};
const WARNING_BADGE_COLOR_MAP = {
  default: {
    borderColor: badgeBorderColors[StatusType.WARNING],
    color: badgeTextColors[StatusType.WARNING],
    backgroundColor: badgeFills[StatusType.WARNING],
    iconColor: badgeIconColors[StatusType.WARNING]
  }
};
const ERROR_BADGE_COLOR_MAP = {
  default: {
    borderColor: badgeBorderColors[StatusType.ERROR],
    color: badgeTextColors[StatusType.ERROR],
    backgroundColor: badgeFills[StatusType.ERROR],
    iconColor: badgeIconColors[StatusType.ERROR]
  }
};
const NEW_BADGE_COLOR_MAP = {
  default: {
    borderColor: badgeBorderColors[StatusType.NEW],
    color: badgeTextColors[StatusType.NEW],
    backgroundColor: badgeFills[StatusType.NEW],
    iconColor: badgeIconColors[StatusType.NEW]
  }
};
const INFO_BADGE_COLOR_MAP = {
  default: {
    borderColor: badgeBorderColors[StatusType.INFO],
    color: badgeTextColors[StatusType.INFO],
    backgroundColor: badgeFills[StatusType.INFO],
    iconColor: badgeIconColors[StatusType.INFO]
  }
};
const SCENARIO_BADGE_COLOR_MAP = {
  default: {
    borderColor: badgeBorderColors[StatusType.SCENARIO],
    color: badgeTextColors[StatusType.SCENARIO],
    backgroundColor: badgeFills[StatusType.SCENARIO],
    iconColor: badgeIconColors[StatusType.SCENARIO]
  }
};
const BETA_BADGE_COLOR_MAP = {
  default: {
    borderColor: badgeBorderColors[StatusType.BETA],
    color: badgeTextColors[StatusType.BETA],
    backgroundColor: badgeFills[StatusType.BETA],
    iconColor: badgeIconColors[StatusType.BETA]
  }
};
const TRIAL_BADGE_COLOR_MAP = {
  default: {
    borderColor: badgeBorderColors[StatusType.TRIAL],
    color: badgeTextColors[StatusType.TRIAL],
    backgroundColor: badgeFills[StatusType.TRIAL],
    iconColor: badgeIconColors[StatusType.TRIAL]
  }
};
const BADGE_COLOR_MAP = {
  [StatusType.DEFAULT]: DEFAULT_BADGE_COLOR_MAP,
  [StatusType.SUCCESS]: SUCCESS_BADGE_COLOR_MAP,
  [StatusType.WARNING]: WARNING_BADGE_COLOR_MAP,
  [StatusType.ERROR]: ERROR_BADGE_COLOR_MAP,
  [StatusType.NEW]: NEW_BADGE_COLOR_MAP,
  [StatusType.INFO]: INFO_BADGE_COLOR_MAP,
  [StatusType.SCENARIO]: SCENARIO_BADGE_COLOR_MAP,
  [StatusType.BETA]: BETA_BADGE_COLOR_MAP,
  [StatusType.TRIAL]: TRIAL_BADGE_COLOR_MAP
};
const BADGE_ICON_MAP = {
  [StatusType.SUCCESS]: IconName.CHECK_CIRCLE,
  [StatusType.WARNING]: IconName.WARNING,
  [StatusType.ERROR]: IconName.ERROR,
  [StatusType.NEW]: IconName.NEW,
  [StatusType.INFO]: IconName.INFO,
  [StatusType.BETA]: IconName.BETA,
  [StatusType.TRIAL]: IconName.DEBT_ITEM
};
const composeCssColorBlock = ({
  color,
  backgroundColor,
  borderColor,
  iconColor
}) => css`
  ${color && css`
    color: ${color};
  `};
  ${backgroundColor && css`
    background-color: ${backgroundColor};
  `}
  ${borderColor && css`
    border-color: ${borderColor};
  `}
  ${iconColor && css`
    ${ICON_SELECTOR} {
      color: ${iconColor};
    }
  `}
`;
const composeCssStatesColorBlock = (colorMap) => css`
  ${composeCssColorBlock(colorMap.default)}
`;
const isInteractiveBadge = (badge) => badge.knowledgeBaseUrl !== void 0 || badge.shareFeedbackUrl !== void 0;
const isScenarioOrDefaultBadge = (badge) => badge.statusType === StatusType.DEFAULT || badge.statusType === StatusType.SCENARIO;
const commonStyling = css`
  height: 18px;
  box-sizing: content-box;
  display: inline-flex;
  padding: 2px 4px 2px 4px;
  justify-content: center;
  align-items: center;
  gap: 0;
  border-radius: 999px;
  border: 1px solid;
  ${text2Bold};
`;
const BadgeContainer = styled.div`
  ${commonStyling};
  ${({ $badgeType = StatusType.DEFAULT }) => composeCssStatesColorBlock(BADGE_COLOR_MAP[$badgeType])};
`;
const BADGE_SELECTOR = BadgeContainer.toString;
const InteractiveBadgeContainer = styled.button`
  ${commonStyling};
  cursor: pointer;

  &:hover,
  &:focus {
    background: ${badgeHover[StatusType.BETA]};
    border-color: ${badgeHover[StatusType.BETA]};
  }

  ${({ $badgeType = StatusType.DEFAULT }) => composeCssStatesColorBlock(BADGE_COLOR_MAP[$badgeType])};
  ${({ $isActive }) => $isActive && css`
      background: ${badgeActive[StatusType.BETA]};
      border-color: ${badgeActive[StatusType.BETA]};
    `}
`;
const BadgeLabel = ({ label }) => /* @__PURE__ */ jsx(
  FlexBox,
  {
    paddingX: "xsmall",
    paddingY: "none",
    justify: "center",
    align: "center",
    gap: "small",
    children: label
  }
);
const ReadonlyBadge = ({ label, statusType, iconName }) => /* @__PURE__ */ jsxs(
  BadgeContainer,
  {
    style: { cursor: "default", whiteSpace: "nowrap" },
    $badgeType: statusType,
    children: [
      iconName && /* @__PURE__ */ jsx(Icon, { iconName, iconSize: IconSize.SMALL }),
      /* @__PURE__ */ jsx(BadgeLabel, { label })
    ]
  }
);
const InteractiveBadgeButton = forwardRef(({ onMenuOpen, isMenuOpened }, ref) => /* @__PURE__ */ jsxs(
  InteractiveBadgeContainer,
  {
    $badgeType: StatusType.BETA,
    $isActive: isMenuOpened,
    onClick: onMenuOpen,
    ref,
    children: [
      /* @__PURE__ */ jsx(
        Icon,
        {
          iconSize: IconSize.SMALL,
          iconName: BADGE_ICON_MAP[StatusType.BETA]
        }
      ),
      /* @__PURE__ */ jsx(BadgeLabel, { label: "Beta" }),
      /* @__PURE__ */ jsx(FlexBox, { justify: "center", align: "center", gap: "small", children: /* @__PURE__ */ jsx(
        Icon,
        {
          iconSize: IconSize.SMALL,
          iconName: isMenuOpened ? IconName.ARROW_UP : IconName.ARROW_DOWN
        }
      ) })
    ]
  }
));
const InteractiveBetaBadge = ({
  knowledgeBaseUrl,
  shareFeedbackUrl,
  trackEvent
}) => {
  return /* @__PURE__ */ jsx(
    ComponentWithDropdown,
    {
      Component: InteractiveBadgeButton,
      options: [
        ...knowledgeBaseUrl ? [
          {
            type: DropdownOptionType.OPTION,
            label: "Why am I seeing this?",
            iconName: IconName.KNOWLEDGE_BASE,
            onClick: () => {
              trackEvent == null ? void 0 : trackEvent("Interactive beta badge (knowledge base)");
              window.open(knowledgeBaseUrl, "_blank");
            }
          }
        ] : [],
        ...shareFeedbackUrl ? [
          {
            type: DropdownOptionType.OPTION,
            label: "Share feedback",
            iconName: IconName.PRESENTATION,
            rightContent: /* @__PURE__ */ jsx(OpenInNewIcon, { color: colors.icon }),
            onClick: () => {
              trackEvent == null ? void 0 : trackEvent("Interactive beta badge (share feedback)");
              window.open(shareFeedbackUrl, "_blank");
            }
          }
        ] : []
      ]
    }
  );
};
const BetaBadge = (props) => {
  return isInteractiveBadge(props) ? /* @__PURE__ */ jsx(InteractiveBetaBadge, { ...props }) : /* @__PURE__ */ jsx(
    ReadonlyBadge,
    {
      label: "Beta",
      statusType: StatusType.BETA,
      iconName: BADGE_ICON_MAP[StatusType.BETA]
    }
  );
};
const NewBadge = (props) => {
  return /* @__PURE__ */ jsx(
    ReadonlyBadge,
    {
      label: props.label ?? "New",
      statusType: StatusType.NEW,
      iconName: BADGE_ICON_MAP[StatusType.NEW]
    }
  );
};
const TrialBadge = () => {
  return /* @__PURE__ */ jsx(
    ReadonlyBadge,
    {
      label: "Trial",
      statusType: StatusType.TRIAL,
      iconName: BADGE_ICON_MAP[StatusType.TRIAL]
    }
  );
};
const Badge = (props) => {
  return /* @__PURE__ */ jsx(
    ReadonlyBadge,
    {
      label: props.label,
      statusType: props.statusType,
      iconName: isScenarioOrDefaultBadge(props) ? props.iconName : BADGE_ICON_MAP[props.statusType]
    }
  );
};
const CustomBadge = (props) => {
  return /* @__PURE__ */ jsx(
    ReadonlyBadge,
    {
      label: props.label,
      statusType: props.statusType,
      iconName: props.iconName
    }
  );
};
const IconContainer = styled.span`
  display: flex;
  align-items: center;
  margin-right: ${s4};
  color: ${colors.icon};
`;
const Label = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const Container$1 = styled.div`
  ${text2};
  color: ${colors.textDefault};
  display: inline-flex;
  height: ${s32};
  border: 1px solid ${colors.borderAction};
  border-radius: ${s16};
  align-items: center;
  padding: 0 ${s16};
  cursor: pointer;
  vertical-align: bottom;
  ${fade};
  background-color: ${colors.surfaceDefault};

  /* Focus ring */
  outline: none;

  ${focusableCSS}
  &&::before {
    border-radius: ${s32};
  }

  ${(props) => props.$isActive ? css`
          color: ${colors.textOnInvertedStrong};
          background: ${colors.surfaceInvertedSubtle};
          border-color: ${colors.borderAction};

          ${ICON_SELECTOR} {
            color: ${colors.iconOnInvertedStrong};
          }

          &:hover {
            border: 1px solid ${colors.borderActionHover};
          }
        ` : css`
          &:hover {
            border: 1px solid ${colors.borderAction};
            background-color: ${colors.surfaceStatusNeutral};
          }
        `}
  ${(props) => props.$isDisabled && css`
      pointer-events: none;
      border: 1px solid ${colors.borderDisabled};
      color: ${colors.textDisabled};
    `}
`;
const FilterChipContainer = styled(Container$1)`
  padding: 0 ${s8};
  gap: ${s4};
  cursor: default;

  & > :last-child {
    cursor: pointer;
  }
`;
function isOverflowing(el) {
  if (!el) return false;
  return el.scrollWidth > el.offsetWidth;
}
const Chip = ({
  className,
  icon,
  iconName,
  isActive,
  isDisabled,
  children,
  onClick,
  popoverText,
  style,
  dataTestId,
  ...rest
  // For backwards compatibility of DIV props
}) => {
  const labelRef = useRef(null);
  const [isPopoverVisible, setPopoverVisibility] = useState(false);
  useEffect(() => {
    setPopoverVisibility(!!popoverText && isOverflowing(labelRef == null ? void 0 : labelRef.current));
  }, [labelRef, popoverText]);
  return /* @__PURE__ */ jsxs(
    Container$1,
    {
      tabIndex: onClick && !isDisabled ? 0 : void 0,
      role: "button",
      className,
      style,
      onClick,
      onKeyDown: ({ key }) => ["Enter", " "].includes(key) && (onClick == null ? void 0 : onClick()),
      "data-test-id": `filter-chip${dataTestId ? `-${dataTestId}` : ""}`,
      $isActive: isActive,
      $isDisabled: isDisabled,
      ...isPopoverVisible && withPlainTextPopover(popoverText),
      ...rest,
      children: [
        icon && /* @__PURE__ */ jsx(IconContainer, { children: icon }),
        iconName && /* @__PURE__ */ jsx(IconContainer, { children: /* @__PURE__ */ jsx(Icon, { iconName, iconSize: IconSize.MEDIUM }) }),
        /* @__PURE__ */ jsx(Label, { ref: labelRef, children })
      ]
    }
  );
};
const FilterChip = ({
  iconName,
  children,
  onClose,
  isDisabled,
  role
}) => /* @__PURE__ */ jsxs(
  FilterChipContainer,
  {
    tabIndex: 1,
    $isDisabled: isDisabled,
    $isActive: !isDisabled,
    onKeyDown: !isDisabled ? ({ key }) => ["Enter", " "].includes(key) && onClose() : void 0,
    "aria-disabled": isDisabled,
    role,
    children: [
      iconName && /* @__PURE__ */ jsx(IconContainer, { children: /* @__PURE__ */ jsx(Icon, { iconName, iconSize: IconSize.MEDIUM }) }),
      /* @__PURE__ */ jsx(Label, { children }),
      !isDisabled && /* @__PURE__ */ jsx(CloseIcon, { onClick: onClose, iconSize: IconSize.MEDIUM })
    ]
  }
);
const getGradient = (side) => `
linear-gradient(
  ${side === "left" ? "90deg" : "270deg"},
  ${colors.bgDefault} 0%,
  ${colors.bgDefault} 60%,
  rgba(255, 255, 255, 0) 100%
)
`;
const ChipsListChildrenWrapper = styled(Space).attrs({ $gap: "s8" })`
  overflow: auto;
  align-items: center;
  ${hideScrollbars};
  ${getFocusRingSpreadCSS}
`;
const CHEVRON_WIDTH = "60px";
const ChevronNav = styled.div`
  background: rgb(255, 255, 255);
  width: ${CHEVRON_WIDTH};
  padding: 0 ${s4};

  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  height: 100%;
  color: ${colors.iconAction};
  cursor: pointer;
  transition:
    left 0.2s,
    right 0.2s;
  &:hover {
    color: ${colors.iconActionHover};
  }

  ${({ $side, $isHidden }) => $side === "left" ? css`
          justify-content: start;
          background: ${getGradient($side)};
          left: ${$isHidden ? `-${CHEVRON_WIDTH}` : 0};
        ` : css`
          justify-content: end;
          background: ${getGradient($side)};
          right: ${$isHidden ? `-${CHEVRON_WIDTH}` : 0};
        `}
`;
const ChipsListWrapperBase = styled(Space).attrs({ $gap: "s8" })`
  ${getFocusRingSpreadCSS};
`;
const ChipsListWithChevronWrapper = styled(ChipsListWrapperBase)`
  position: relative;
  overflow: hidden;
`;
const ChipsListWithWrap = styled(ChipsListWrapperBase)`
  flex-wrap: wrap;
`;
const ChipsListWithChevrons = ({ children }) => {
  const rootWrapperRef = useRef(null);
  const { width: rootWrapperWidth = 0 } = useResizeObserver(rootWrapperRef);
  const {
    wrapperRef,
    wrapperOnScroll,
    isLeftChevronVisible,
    isRightChevronVisible,
    onLeftChevronClick,
    onRightChevronClick
  } = useChevronSliders(rootWrapperWidth, children);
  return /* @__PURE__ */ jsxs(ChipsListWithChevronWrapper, { ref: rootWrapperRef, role: "grid", children: [
    /* @__PURE__ */ jsx(
      ChevronNav,
      {
        $side: "left",
        $isHidden: !isLeftChevronVisible,
        onClick: onLeftChevronClick,
        "data-test-id": "chevron-nav-left",
        children: /* @__PURE__ */ jsx(Icon, { iconName: IconName.CHEVRON_LEFT })
      }
    ),
    /* @__PURE__ */ jsx(
      ChipsListChildrenWrapper,
      {
        ref: wrapperRef,
        onScroll: wrapperOnScroll,
        role: "row",
        children
      }
    ),
    /* @__PURE__ */ jsx(
      ChevronNav,
      {
        $side: "right",
        $isHidden: !isRightChevronVisible,
        onClick: onRightChevronClick,
        "data-test-id": "chevron-nav-right",
        children: /* @__PURE__ */ jsx(Icon, { iconName: IconName.CHEVRON_RIGHT })
      }
    )
  ] });
};
const ChipsList = ({
  children,
  overflowType = "chevrons"
}) => {
  const Wrapper2 = overflowType === "chevrons" ? ChipsListWithChevrons : ChipsListWithWrap;
  return /* @__PURE__ */ jsx(Wrapper2, { children });
};
const CHIPS_LIST_SELECTOR = ChipsListWrapperBase.toString();
const ChipsSeparator = styled.div`
  border-left: 1px solid ${colors.borderAction};
  height: 100%;
`;
const FiltersBar = styled.div`
  display: flex;
  justify-content: space-between;

  & > ${CHIPS_LIST_SELECTOR} {
    margin-top: ${-FOCUS_RING_SPREAD + 2}px;
    margin-right: ${s16};
  }
`;
const perTypeColorMap = {
  [StatusType.DEFAULT]: {
    borderColor: colors.borderAction,
    labelColor: colors.textDefault,
    iconColor: colors.icon,
    backgroundColor: colors.surfaceStatusNeutral,
    hoverBorderColor: colors.borderActionHover
  },
  [StatusType.NEW]: {
    borderColor: colors.borderBrand,
    labelColor: colors.textBrand,
    iconColor: colors.iconBrand,
    backgroundColor: colors.surfaceBrand,
    hoverBorderColor: colors.borderBrandStrong
  },
  [StatusType.ERROR]: {
    borderColor: colors.borderError,
    labelColor: colors.textError,
    iconColor: colors.iconError,
    backgroundColor: colors.surfaceError,
    hoverBorderColor: colors.borderErrorStrong
  },
  [StatusType.WARNING]: {
    borderColor: colors.borderWarning,
    labelColor: colors.textWarning,
    iconColor: colors.iconWarning,
    backgroundColor: colors.surfaceWarning,
    hoverBorderColor: colors.borderWarningStrong
  },
  [StatusType.SUCCESS]: {
    borderColor: colors.borderSuccess,
    labelColor: colors.textSuccess,
    iconColor: colors.iconSuccess,
    backgroundColor: colors.surfaceSuccess,
    hoverBorderColor: colors.borderSuccessStrong
  },
  [StatusType.INFO]: {
    borderColor: colors.borderInfo,
    labelColor: colors.textInfo,
    iconColor: colors.iconInfo,
    backgroundColor: colors.surfaceInfo,
    hoverBorderColor: colors.borderInfoStrong
  },
  [StatusType.SCENARIO]: {
    borderColor: colors.surfaceScenario,
    labelColor: colors.textOnInvertedStrong,
    backgroundColor: colors.surfaceScenario
  },
  [StatusType.BETA]: {
    borderColor: colors.surfaceActionPrimary,
    labelColor: colors.textOnInvertedStrong,
    iconColor: colors.textOnInvertedStrong,
    backgroundColor: colors.surfaceActionPrimary
  },
  [StatusType.TRIAL]: {
    borderColor: colors.pink60,
    labelColor: colors.textOnInvertedStrong,
    iconColor: colors.textOnInvertedStrong,
    backgroundColor: colors.pink60
  }
};
const LabelWrapper = styled.div``;
const getColorMapCSS = ({
  $statusType,
  $isClickable,
  $isDisabled,
  $isClosable
}) => {
  const colorMap = $isDisabled ? perTypeColorMap[StatusType.DEFAULT] : perTypeColorMap[$statusType];
  const iconSelector = $isClosable ? `${ICON_SELECTOR}:not(:last-child)` : ICON_SELECTOR;
  return css`
    border-color: ${colorMap.borderColor};
    background-color: ${colorMap.backgroundColor};

    ${iconSelector} {
      color: ${colorMap.iconColor};
    }

    ${LabelWrapper} {
      color: ${colorMap.labelColor};
    }

    ${$isClickable && css`
      &:hover {
        border-color: ${colorMap.hoverBorderColor};
      }
    `}

    ${$isDisabled && css`
      pointer-events: none;
      ${iconSelector} {
        color: ${colors.iconActionDisabled};
      }
      ${LabelWrapper} {
        color: ${colors.textDisabled};
      }
      border-color: ${colors.borderDisabled};
      background-color: ${colors.surfaceDisabled};
    `}
  `;
};
const tagWrapperCss = css`
  padding: 2px 0;
  border: 1px solid red;
  border-radius: ${r4};
  display: inline-flex;
  align-items: center;
  ${normal14};
  line-height: 18px;
  vertical-align: bottom;

  ${ICON_SELECTOR} {
    margin: 0 ${s4};
  }

  ${LabelWrapper} {
    &:first-child {
      margin-left: ${s8};
    }
    &:last-child {
      margin-right: ${s8};
    }
  }

  position: relative;
  outline: none;
  ${({ $isFocused }) => $isFocused && focusRingCSS}
  ${({ $isClickable }) => $isClickable && css`
      cursor: pointer;
    `}

  ${getColorMapCSS}
`;
const TagButtonWrapper = styled.button`
  ${tagWrapperCss}
`;
const TagDivWrapper = styled.div`
  ${tagWrapperCss}
`;
const TAG_DIV_SELECTOR = TagDivWrapper.toString();
const TAG_BUTTON_SELECTOR = TagButtonWrapper.toString();
const TagCloseIcon = styled(CloseIcon).attrs({
  iconSize: IconSize.SMALL
})`
  cursor: pointer;
  color: ${colors.iconAction};
  &:hover {
    color: ${colors.iconActionHover};
  }
`;
const CounterWrapper = styled.strong`
  margin-left: ${s4};
  white-space: nowrap;
`;
const CounterValue = styled.div`
  display: inline-block;
  text-align: center;
  margin: 0 1px;
  min-width: 6px;
`;
const getIconProps = ({ iconName, rightIconName, leftIcon, rightIcon }, side) => {
  if (side === "left") {
    return iconName ? {
      name: iconName
    } : leftIcon ? leftIcon : void 0;
  }
  return rightIconName ? {
    name: rightIconName
  } : rightIcon ? rightIcon : void 0;
};
const tagOperations = {
  getIconProps
};
const Counter = ({ children }) => /* @__PURE__ */ jsxs(CounterWrapper, { children: [
  "(",
  /* @__PURE__ */ jsx(CounterValue, { children }),
  ")"
] });
const BaseTag = (props) => {
  const {
    label,
    count,
    statusType,
    onCloseClick,
    onClick,
    onMouseDown,
    showDragHandle,
    children,
    reactSortableRef,
    dragTargetClass,
    isDisabled,
    style,
    className,
    type = "button",
    LeftIconComponent,
    [POPOVER_ID_ATTR]: popoverId
  } = props;
  const { isFocusVisible, focusProps } = useFocusRing();
  const handleCloseClick = (event) => {
    event.stopPropagation();
    onCloseClick == null ? void 0 : onCloseClick();
  };
  const leftIcon = tagOperations.getIconProps(props, "left");
  const rightIcon = tagOperations.getIconProps(props, "right");
  const Wrapper2 = onClick ? TagButtonWrapper : TagDivWrapper;
  return /* @__PURE__ */ jsxs(
    Wrapper2,
    {
      $statusType: statusType || StatusType.DEFAULT,
      onClick,
      onMouseDown,
      ...focusProps,
      tabIndex: onClick ? 0 : -1,
      $isFocused: isFocusVisible,
      $isClickable: Boolean(onClick || onMouseDown),
      $isClosable: Boolean(onCloseClick),
      ref: reactSortableRef,
      role: onClick ? "button" : void 0,
      type: onClick ? type : void 0,
      $isDisabled: isDisabled,
      style,
      className,
      ...popoverId ? { [POPOVER_ID_ATTR]: popoverId } : {},
      children: [
        showDragHandle && /* @__PURE__ */ jsx(
          Icon,
          {
            iconName: IconName.DRAG_INDICATOR,
            iconSize: IconSize.SMALL,
            className: dragTargetClass
          }
        ),
        leftIcon && /* @__PURE__ */ jsx(
          Icon,
          {
            iconName: leftIcon.name,
            iconSize: IconSize.SMALL,
            style: { color: leftIcon == null ? void 0 : leftIcon.color }
          }
        ),
        LeftIconComponent,
        (label || children || count) && /* @__PURE__ */ jsxs(LabelWrapper, { className: dragTargetClass, children: [
          label,
          (typeof count === "number" || count) && /* @__PURE__ */ jsx(Counter, { children: count }),
          children
        ] }),
        rightIcon && /* @__PURE__ */ jsx(
          Icon,
          {
            iconName: rightIcon.name,
            iconSize: IconSize.SMALL,
            style: { color: rightIcon == null ? void 0 : rightIcon.color }
          }
        ),
        onCloseClick && /* @__PURE__ */ jsx(
          TagCloseIcon,
          {
            role: "button",
            "aria-label": label ? `Remove ${label}` : "Remove",
            onClick: handleCloseClick,
            onMouseDownCapture: (evt) => evt.stopPropagation()
          }
        )
      ]
    }
  );
};
const Tag = (props) => {
  return /* @__PURE__ */ jsx(BaseTag, { ...props });
};
const TagButton = (props) => {
  return /* @__PURE__ */ jsx(BaseTag, { ...props });
};
const SPACING = s4;
const TagGroup = styled.div`
  margin-top: -${SPACING};
  margin-left: -${SPACING};

  ${TAG_DIV_SELECTOR}, ${TAG_BUTTON_SELECTOR} {
    margin-top: ${SPACING};
    margin-left: ${SPACING};
  }
`;
const NotificationWrapper = styled.div`
  max-width: ${(props) => props.$maxWidth}px;
  width: 100%;
  align-self: center;
  display: flex;
  flex-direction: column;
  gap: ${s16};
`;
const NotificationContainer$1 = styled.div`
  ${text2};
  display: flex;
  gap: ${s16};
  border-radius: 4px;
  padding: ${s16};

  ${(props) => css`
    background: ${props.$backgroundColor};
    border: 1px solid ${props.$borderColor};
    color: ${props.$textColor};
    align-items: ${props.$alignItems};

    > ${ICON_SELECTOR} {
      color: ${props.$iconColor};
    }
  `}
  ${({ $maxWidth }) => $maxWidth && css`
      max-width: ${$maxWidth};
    `}
`;
const NOTIFICATION_SELECTOR = NotificationContainer$1.toString();
const ContentContainer = styled.div`
  flex: 1;
  word-break: break-word;
  margin: auto 0;
`;
const BaseNotification = ({
  icon,
  children,
  backgroundColor,
  borderColor,
  iconColor,
  textColor,
  maxWidth,
  alignItems,
  ...rest
}) => {
  return /* @__PURE__ */ jsxs(
    NotificationContainer$1,
    {
      $backgroundColor: backgroundColor,
      $borderColor: borderColor,
      $iconColor: iconColor,
      $textColor: textColor,
      $maxWidth: maxWidth,
      $alignItems: alignItems,
      role: "status",
      ...rest,
      children: [
        icon,
        /* @__PURE__ */ jsx(ContentContainer, { children })
      ]
    }
  );
};
const DefaultNotification = ({
  children,
  icon,
  ...rest
}) => {
  return /* @__PURE__ */ jsx(
    BaseNotification,
    {
      backgroundColor: colors.surfaceStatusNeutral,
      borderColor: colors.borderAction,
      iconColor: colors.icon,
      textColor: colors.textDefault,
      icon: icon === void 0 ? /* @__PURE__ */ jsx(InfoIcon, {}) : icon,
      ...rest,
      children
    }
  );
};
const InfoNotification = ({
  children,
  icon,
  ...rest
}) => {
  return /* @__PURE__ */ jsx(
    BaseNotification,
    {
      backgroundColor: colors.surfaceInfo,
      borderColor: colors.borderInfo,
      iconColor: colors.iconInfo,
      textColor: colors.textInfo,
      icon: icon === void 0 ? /* @__PURE__ */ jsx(InfoIcon, {}) : icon,
      ...rest,
      children
    }
  );
};
const WarningNotification = ({
  children,
  icon,
  ...rest
}) => {
  return /* @__PURE__ */ jsx(
    BaseNotification,
    {
      backgroundColor: colors.surfaceWarning,
      borderColor: colors.borderWarning,
      iconColor: colors.iconWarning,
      textColor: colors.textWarning,
      icon: icon === void 0 ? /* @__PURE__ */ jsx(WarningIcon, {}) : icon,
      ...rest,
      children
    }
  );
};
const ErrorNotification = ({
  children,
  icon,
  ...rest
}) => {
  return /* @__PURE__ */ jsx(
    BaseNotification,
    {
      role: "alert",
      backgroundColor: colors.surfaceError,
      borderColor: colors.borderError,
      iconColor: colors.iconError,
      textColor: colors.textError,
      icon: icon === void 0 ? /* @__PURE__ */ jsx(ErrorIcon, {}) : icon,
      ...rest,
      children
    }
  );
};
const SuccessNotification = ({
  children,
  icon,
  ...rest
}) => {
  return /* @__PURE__ */ jsx(
    BaseNotification,
    {
      backgroundColor: colors.surfaceSuccess,
      borderColor: colors.borderSuccess,
      iconColor: colors.iconSuccess,
      textColor: colors.textSuccess,
      icon: icon === void 0 ? /* @__PURE__ */ jsx(CheckCircleIcon, {}) : icon,
      ...rest,
      children
    }
  );
};
const SaveNotification = ({
  children,
  icon,
  ...rest
}) => {
  return /* @__PURE__ */ jsx(
    BaseNotification,
    {
      backgroundColor: colors.surfaceStatusNeutral,
      borderColor: colors.borderAction,
      iconColor: colors.icon,
      textColor: colors.textDefault,
      icon: icon === void 0 ? /* @__PURE__ */ jsx(SaveIcon, {}) : icon,
      ...rest,
      children
    }
  );
};
const PreviewNotification = ({
  children,
  icon,
  ...rest
}) => {
  return /* @__PURE__ */ jsx(
    BaseNotification,
    {
      backgroundColor: colors.surfaceBrand,
      borderColor: colors.borderBrand,
      iconColor: colors.iconBrand,
      textColor: colors.textBrand,
      icon: icon === void 0 ? /* @__PURE__ */ jsx(VisibilityIcon, {}) : icon,
      ...rest,
      children
    }
  );
};
const NewFeatureNotification = ({
  children,
  icon,
  ...rest
}) => {
  return /* @__PURE__ */ jsx(
    BaseNotification,
    {
      backgroundColor: colors.surfaceBrand,
      borderColor: colors.borderBrand,
      iconColor: colors.iconBrand,
      textColor: colors.textInfo,
      icon: icon === void 0 ? /* @__PURE__ */ jsx(ProductUpdatesIcon, {}) : icon,
      alignItems: "center",
      ...rest,
      children
    }
  );
};
const ParseAPIErrorNotification = ({
  error,
  maxWidth
}) => /* @__PURE__ */ jsxs(ErrorNotification, { maxWidth, children: [
  error.userMessage,
  error.traceId ? /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("br", {}),
    `Trace ID: ${error.traceId}`
  ] }) : ""
] });
const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${s8};
  color: ${colors.textDefault};
`;
const DetailsHeader = styled.div`
  display: flex;
  justify-content: flex-end;
`;
const ExpanderButton = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${s4};
  padding-left: ${s8};
  ${semibold14}
  color: ${colors.textModerate};
  cursor: pointer;
  user-select: none;

  &:focus {
    outline: none;
    ${focusRingCSS}
  }
`;
const NotificationDetails = ({
  label = "Details",
  isInitiallyOpen = false,
  children
}) => {
  const [isExpanded, setExpanded] = useState(isInitiallyOpen);
  const ref = useRef(null);
  const { buttonProps } = useButton(
    {
      elementType: "div",
      onPress: () => setExpanded(!isExpanded)
    },
    ref
  );
  return /* @__PURE__ */ jsxs(DetailsContainer, { children: [
    /* @__PURE__ */ jsx(DetailsHeader, { children: /* @__PURE__ */ jsxs(ExpanderButton, { ref, ...buttonProps, children: [
      label,
      /* @__PURE__ */ jsx(AnimatedChevron, { $isUp: isExpanded })
    ] }) }),
    isExpanded && children
  ] });
};
const ClosableNotification = ({
  dontShowAgain,
  setDontShowAgain,
  children,
  dismissNotification
}) => {
  return /* @__PURE__ */ jsxs(Container, { padding: "medium", gap: "small", align: "center", children: [
    /* @__PURE__ */ jsx(Icon, { iconName: IconName.INFO, color: colors.icon }),
    /* @__PURE__ */ jsx(BannerText, { children }),
    /* @__PURE__ */ jsx(Checkbox, { isChecked: dontShowAgain, onChange: setDontShowAgain, children: /* @__PURE__ */ jsx(CheckboxLabel, { children: "Don't show this again" }) }),
    /* @__PURE__ */ jsx(GhostButton, { onClick: dismissNotification, children: /* @__PURE__ */ jsx(Icon, { iconName: IconName.CLOSE }) })
  ] });
};
const Container = styled(FlexBox)`
  border: 1px solid ${colors.borderAction};
  background-color: ${colors.surfaceStatusNeutral};
  border-radius: ${s4};
`;
const CheckboxLabel = styled.span`
  ${caption};
  white-space: nowrap;
`;
const BannerText = styled.span`
  margin-right: auto;
`;
const NotificationContainer = styled(FlexBox)`
  pointer-events: auto;
  max-width: 859px;
`;
const ActionButton = styled(SecondaryButton)`
  flex-shrink: 0;
`;
const InteractiveNotification = ({
  config,
  onClose,
  onActionButtonClick,
  notificationBodyStyle
}) => {
  const { title, description, badgeConfig, actionButtonText, iconConfig } = config;
  return /* @__PURE__ */ jsxs(
    NotificationContainer,
    {
      gap: "small",
      align: "center",
      paddingY: "medium",
      paddingRight: "medium",
      paddingLeft: "large",
      borderColor: "borderSubtle00",
      borderRadius: "r8",
      backgroundColor: "surfaceDefault",
      style: notificationBodyStyle,
      children: [
        iconConfig ? /* @__PURE__ */ jsx(
          Icon,
          {
            iconName: iconConfig.iconName,
            iconSize: iconConfig == null ? void 0 : iconConfig.iconSize,
            color: iconConfig.color
          }
        ) : null,
        /* @__PURE__ */ jsxs(Stack, { flex: 1, children: [
          /* @__PURE__ */ jsxs(FlexBox, { gap: "small", align: "center", flex: 1, children: [
            badgeConfig && /* @__PURE__ */ jsx(Badge, { ...badgeConfig }),
            title ? /* @__PURE__ */ jsx(Header4, { children: title }) : null
          ] }),
          /* @__PURE__ */ jsx(Box, { marginTop: "small", children: /* @__PURE__ */ jsx(Text, { variant: "text2", children: description }) })
        ] }),
        onActionButtonClick && actionButtonText ? /* @__PURE__ */ jsx(ActionButton, { onClick: onActionButtonClick, children: actionButtonText }) : null,
        /* @__PURE__ */ jsx(GhostButton, { onClick: onClose, children: /* @__PURE__ */ jsx(Icon, { iconName: IconName.CLOSE }) })
      ]
    }
  );
};
const Notification = ({
  info,
  success,
  warning,
  error,
  children,
  details,
  detailsLabel,
  ...props
}) => {
  const kids = /* @__PURE__ */ jsxs(Fragment, { children: [
    children,
    details && /* @__PURE__ */ jsx(NotificationDetails, { label: detailsLabel, children: details })
  ] });
  if (error) {
    return /* @__PURE__ */ jsx(ErrorNotification, { ...props, children: kids });
  } else if (warning) {
    return /* @__PURE__ */ jsx(WarningNotification, { ...props, children: kids });
  } else if (success) {
    return /* @__PURE__ */ jsx(SuccessNotification, { ...props, children: kids });
  } else if (info) {
    return /* @__PURE__ */ jsx(InfoNotification, { ...props, children: kids });
  }
  return /* @__PURE__ */ jsx(DefaultNotification, { ...props, children: kids });
};
const Wrapper = styled.div`
  position: relative;
  display: block;
`;
const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 0.5);
  z-index: 1;
`;
LoadingOverlay.displayName = "LoadingOverlay";
const LoadingWrapper = ({
  loading = false,
  children,
  ...props
}) => /* @__PURE__ */ jsxs(Wrapper, { ...props, children: [
  children,
  loading && /* @__PURE__ */ jsx(LoadingOverlay, { children: /* @__PURE__ */ jsx(SpinnerIcon, { role: "progressbar" }) })
] });
LoadingWrapper.displayName = "LoadingWrapper";
const TOAST_HEIGHT_PLUS_GAP = 82;
const getFadeOut = () => `
  from {
    top: 0;
    opacity: 1;
  }
  to {
    top: -${s16};
    opacity: 0;
    }
`;
const getSlideDown = () => `
  from {
    top: -${TOAST_HEIGHT_PLUS_GAP}px;
  }
  to {
    top: 0;
  }
`;
const getSlideOut = () => `
// This one is used when there are too many toasts
  from {
    top: -${TOAST_HEIGHT_PLUS_GAP}px;
    opacity: 1;
  }
  to {
    top: ${TOAST_HEIGHT_PLUS_GAP / 2}px;
    opacity: 0;
  }
`;
const getSlideUp = () => `
  from {
    top: ${TOAST_HEIGHT_PLUS_GAP}px;
  }
  to {
    top: 0;
  }
`;
const getKeyframes = () => css`
  @keyframes slideDown1 {
    /* needs two different names because it won't trigger if animation with the same name is applied twice */
    ${getSlideDown()}
  }
  @keyframes slideDown2 {
    ${getSlideDown()}
  }
  @keyframes slideIn {
    ${getSlideDown()}
  }
  @keyframes slideOut {
    ${getSlideOut()}
  }
  @keyframes fadeOut {
    ${getFadeOut()}
  }
  @keyframes slideUp0 {
    ${getSlideUp()}
  }
  @keyframes slideUp1 {
    ${getSlideUp()}
  }
`;
const TOAST_DURATION = 5e3;
const ANIMATION_START_DURATION = 500;
const ANIMATION_END_DURATION = 400;
const getAnimationCSS = ({
  index,
  shouldMoveUp,
  dontAnimate
}) => {
  let animationName = "";
  if (dontAnimate) return "";
  else if (shouldMoveUp) {
    animationName = `slideUp${index}`;
  } else if (index === 0) {
    animationName = "slideIn";
  } else if (index <= 2) {
    animationName = `slideDown${index}`;
  } else {
    animationName = "slideOut";
  }
  return css`
    animation:
      ${ANIMATION_START_DURATION}ms ${animationName},
      ${ANIMATION_END_DURATION}ms fadeOut
        ${TOAST_DURATION - ANIMATION_END_DURATION}ms;
  `;
};
const ToastWrapper = styled.div`
  width: fit-content;
  max-width: 400px;
  box-shadow: ${shadowM};
  position: relative;
  border-radius: ${radius.r4};
  pointer-events: auto; /* So that children of ToastsContainer can be clicked */
  ${({ $index, $shouldMoveUp, $dontAnimate }) => getAnimationCSS({
  index: $index,
  shouldMoveUp: $shouldMoveUp,
  dontAnimate: $dontAnimate
})};
  ${getKeyframes()}
`;
const CloseButton = (props) => {
  return /* @__PURE__ */ jsx(GhostButton, { buttonSize: ButtonSize.SMALL, ...props, children: /* @__PURE__ */ jsx(CloseIcon, {}) });
};
const ToastBodyLayout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${s16};
  ${BUTTON_SELECTOR} {
    /* NB. These are needed until Notification handles the close button itself */
    margin-top: -${s4};
    margin-bottom: -${s4};
  }
`;
const Toast = ({
  info,
  success,
  error,
  message,
  removeToast,
  index,
  shouldMoveUp,
  dontAnimate
}) => {
  return /* @__PURE__ */ jsx(
    ToastWrapper,
    {
      $index: index,
      $shouldMoveUp: shouldMoveUp,
      $dontAnimate: dontAnimate,
      "data-test-id": "toast-notification",
      children: /* @__PURE__ */ jsx(Notification, { info, success, error, children: /* @__PURE__ */ jsxs(ToastBodyLayout, { children: [
        message,
        /* @__PURE__ */ jsx(CloseButton, { onClick: removeToast })
      ] }) })
    }
  );
};
var ToastType = /* @__PURE__ */ ((ToastType2) => {
  ToastType2["INFO"] = "info";
  ToastType2["SUCCESS"] = "success";
  ToastType2["ERROR"] = "error";
  return ToastType2;
})(ToastType || {});
const action$ = new Subject();
const errorSubject = new Subject();
errorSubject.subscribe(logError);
const defaultToast = {
  error: false,
  id: 0,
  index: 0,
  info: false,
  message: "",
  removeToast: () => null,
  shouldMoveUp: false,
  success: false,
  type: ToastType.INFO
};
const showToast = (message, type = ToastType.INFO) => {
  action$.next(
    toastReceived({
      ...defaultToast,
      id: Math.random(),
      message,
      type
    })
  );
};
const toastReceived = actionCreator("[ToastManager] TOAST_RECEIVED");
const toastExpired = actionCreator("[ToastManager] TOAST_TIMED_OUT");
const manyToastsVisible = actionCreator("[ToastManager] TOAST_TIMED_OUT");
const toastReceivedReducer = ({ toasts, idIndexMap }, toast) => toViewProps({
  toasts: [toast, ...toasts],
  idIndexMap
});
const toastExpiredReducer = ({ toasts, idIndexMap }, id) => toViewProps({
  toasts: toasts.filter((toast) => toast.id !== id),
  idIndexMap
});
const manyToastVisibleReducer = ({ toasts, idIndexMap }) => toViewProps({
  toasts: toasts.slice(0, -1),
  idIndexMap
});
const toViewProps = ({ toasts, idIndexMap }) => {
  const allIds = new Set(toasts.map(({ id }) => id));
  const newIdIndexMap = Object.fromEntries(
    Object.entries(idIndexMap).filter(([id]) => allIds.has(Number(id)))
  );
  const updatedToasts = toasts.map(({ id, type, message }, index) => {
    const shouldMoveUp = idIndexMap[id] > index;
    newIdIndexMap[id] = index;
    return {
      error: type === ToastType.ERROR,
      id,
      index,
      info: type === ToastType.INFO,
      message,
      removeToast: () => action$.next(toastExpired(id)),
      shouldMoveUp,
      success: type === ToastType.SUCCESS,
      type
    };
  });
  return {
    toasts: updatedToasts,
    idIndexMap: newIdIndexMap
  };
};
const expireToasts = ([{ id }, { toasts }]) => {
  setTimeout(
    () => action$.next(toastExpired(id)),
    TOAST_DURATION - ANIMATION_END_DURATION / 2
  );
  if (toasts.length > 2) {
    setTimeout(
      () => action$.next(manyToastsVisible()),
      ANIMATION_END_DURATION - 100
    );
  }
};
const toast$ = reducedStream(
  "toast$",
  { toasts: [], idIndexMap: {} },
  [
    reducer(toastReceived, toastReceivedReducer),
    reducer(toastExpired, toastExpiredReducer),
    reducer(manyToastsVisible, manyToastVisibleReducer)
  ],
  {
    action$,
    errorSubject
  }
);
const toastRoutine = action$.pipe(
  ofType(toastReceived),
  extractPayload(),
  withLatestFrom(toast$),
  tap(expireToasts)
);
const subscribe = () => {
  toastRoutine.subscribe({
    error(error) {
      errorSubject.next(error);
      subscribe();
    }
  });
};
subscribe();
const DEFAULT_OFFSET_TOP = 112;
const ToastsContainer = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;
  top: 0;
  right: ${s32};
  padding-top: ${s16};
  padding-left: ${s16}; /* needs some padding here so that the box shadow of the toast is not clipped */
  gap: ${s16};
  z-index: ${zIndex.TOAST};
  align-items: flex-end;
  height: 400px; /* needs some height so that the toast that are pushed have some space to disappear into */
  overflow: hidden;
  pointer-events: none; /* to avoid blocking user interaction */
`;
const ToastManagerContent = ({
  offsetTop = DEFAULT_OFFSET_TOP,
  toasts
}) => toasts.length ? /* @__PURE__ */ jsx(ToastsContainer, { style: { top: offsetTop }, children: toasts.map((props) => /* @__PURE__ */ createElement(Toast, { ...props, key: props.id })) }) : null;
const ToastManager = connect(ToastManagerContent, toast$);
const EmptyStateMediaSize = {
  small: "110px",
  large: "175px"
};
const MaxWidthContainer = styled.div`
  max-width: 500px;
`;
const MediaContainer = styled.div`
  width: ${({ $size }) => $size === "small" ? EmptyStateMediaSize.small : EmptyStateMediaSize.large};
`;
const EmptyStateActions = (_) => {
  useRef(() => {
    throw new Error("EmptyStateActions needs to be inside <EmptyState/>");
  });
  return /* @__PURE__ */ jsx(Fragment, {});
};
const PrivateEmptyStateActions = ({
  children,
  align
}) => /* @__PURE__ */ jsx(Stack, { gap: "medium", align, children });
const EmptyState = ({
  media,
  title,
  description,
  layout = "horizontal",
  size = "large",
  children
}) => {
  const LayoutContent = layout === "horizontal" ? FlexBox : Stack;
  return /* @__PURE__ */ jsx(FlexBox, { justify: layout === "horizontal" ? "start" : "center", children: /* @__PURE__ */ jsx(MaxWidthContainer, { children: /* @__PURE__ */ jsxs(LayoutContent, { gap: "medium", align: "center", children: [
    /* @__PURE__ */ jsx(MediaContainer, { $size: size, children: media }),
    /* @__PURE__ */ jsxs(
      Stack,
      {
        gap: "medium",
        align: layout === "vertical" ? "center" : "start",
        children: [
          /* @__PURE__ */ jsxs(
            Stack,
            {
              gap: "small",
              align: layout === "vertical" ? "center" : "start",
              children: [
                /* @__PURE__ */ jsx(Header3, { align: layout === "vertical" ? "center" : "left", children: title }),
                /* @__PURE__ */ jsx(
                  Paragraph,
                  {
                    align: layout === "vertical" ? "center" : "left",
                    variant: "text2",
                    children: description
                  }
                )
              ]
            }
          ),
          Children.toArray(children).map((child) => {
            if (typeof child === "object" && isValidElement(child) && child.type === EmptyStateActions) {
              return /* @__PURE__ */ jsx(
                PrivateEmptyStateActions,
                {
                  align: layout === "vertical" ? "center" : "start",
                  ...child.props
                },
                child.key
              );
            }
            return child;
          })
        ]
      }
    )
  ] }) }) });
};
const EmptyStateAddDashboardIllustration = () => {
  return /* @__PURE__ */ jsxs("svg", { viewBox: "0 0 176 175", children: [
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "#D9E5FF",
        fillRule: "evenodd",
        d: "M18.678 39.038h117.935c2.663 0 6.087 1.902 6.087 6.087v50.287a31.666 31.666 0 0 0-7.228-.83c-17.439 0-31.576 14.136-31.576 31.575 0 2.487.287 4.907.831 7.228H18.678c-1.521 0-6.847-.38-6.847-6.087V45.886c0-5.327 3.424-6.848 6.847-6.848Z",
        clipRule: "evenodd"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "#fff",
        fillRule: "evenodd",
        d: "M126.824 50.093a3.693 3.693 0 0 1 3.695-3.692 3.693 3.693 0 1 1 0 7.384 3.67 3.67 0 0 1-1.492-.32l-9.612 8.25a.76.76 0 0 0 .009.082c.005.037.01.072.01.11a3.693 3.693 0 0 1-7.386.042l-6.901-3.457a3.674 3.674 0 0 1-2.71 1.2c-.384 0-.746-.076-1.094-.184l-7.31 8.395c.174.428.275.896.275 1.387a3.694 3.694 0 0 1-3.696 3.691 3.694 3.694 0 0 1-3.695-3.691 3.694 3.694 0 0 1 3.695-3.692c.457 0 .887.094 1.29.245l7.194-8.288A3.656 3.656 0 0 1 98.742 56a3.694 3.694 0 0 1 3.695-3.692 3.683 3.683 0 0 1 3.669 3.43l7.055 3.53a3.678 3.678 0 0 1 2.578-1.053c.871 0 1.659.313 2.291.816l9.007-7.75a3.661 3.661 0 0 1-.213-1.188Zm-72.802 8.793c0-.634.514-1.149 1.148-1.149 15.702.164 28.396 12.857 28.559 28.558a1.15 1.15 0 0 1-1.148 1.15c-.036 0-.07-.01-.102-.019a.544.544 0 0 0-.066-.015v.033H55.17a1.15 1.15 0 0 1-1.148-1.149V59.053h.034c-.004-.022-.01-.044-.016-.066a.37.37 0 0 1-.018-.1Zm22.67 33.922a1.14 1.14 0 0 0-1.134-1.144h-24.75c-.302 0-.59-.12-.803-.335a1.147 1.147 0 0 1-.333-.808V65.692h-.033a.559.559 0 0 1 .015-.066.369.369 0 0 0 .018-.1 1.14 1.14 0 0 0-1.135-1.144c-.015 0-.028.004-.041.008-.012.003-.023.007-.036.008v-.008a4.73 4.73 0 0 1-.14-.004c-.054-.002-.108-.004-.162-.004-15.759 0-28.535 12.863-28.535 28.73s12.776 28.73 28.535 28.73 28.535-12.863 28.535-28.73c0-.057-.002-.113-.004-.17a8.839 8.839 0 0 1-.002-.104c0-.011.006-.02.006-.03Zm56.592-19.9h-6.167c-.818 0-1.602.285-2.18.792-.578.507-.903 1.194-.903 1.91V96.81a32.197 32.197 0 0 1 12.333-2.226V75.61c0-.716-.325-1.403-.903-1.91-.578-.507-1.363-.791-2.18-.791Zm-15.417 18.908v8.238c-5.715 3.853-10.1 9.49-12.334 16.101V91.816c0-.716.325-1.403.904-1.91.578-.506 1.362-.79 2.18-.79h6.167c.817 0 1.602.284 2.18.79.578.507.903 1.194.903 1.91Zm-21.584 13.506h-6.166c-.818 0-1.602.284-2.18.791-.579.506-.904 1.193-.904 1.91v13.483h12.334v-13.483c0-.717-.325-1.404-.903-1.91-.579-.507-1.363-.791-2.18-.791Z",
        clipRule: "evenodd"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "#9DB9F2",
        d: "M163.169 126.071c0 15.128-12.263 27.392-27.391 27.392-15.128 0-27.391-12.264-27.391-27.392 0-15.127 12.263-27.39 27.391-27.39 15.128 0 27.391 12.263 27.391 27.39Z"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "#fff",
        fillRule: "evenodd",
        d: "M137.987 115.026a2.209 2.209 0 1 0-4.418 0v8.836h-8.836a2.209 2.209 0 1 0 0 4.418h8.836v8.836a2.209 2.209 0 1 0 4.418 0v-8.836h8.836a2.209 2.209 0 1 0 0-4.418h-8.836v-8.836Z",
        clipRule: "evenodd"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "#D9E5FF",
        d: "M40.645 27.428a5.89 5.89 0 1 1-11.781 0 5.89 5.89 0 0 1 11.781 0Z"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "#9DB9F2",
        d: "M98.775 143.911a4.11 4.11 0 1 1 6.643-4.844 4.111 4.111 0 0 1-6.643 4.844Z"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "#D9E5FF",
        d: "M51.69 33.319a2.945 2.945 0 1 1-5.89 0 2.945 2.945 0 0 1 5.89 0Z"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "#9DB9F2",
        d: "M106.927 149.202a2.056 2.056 0 1 1 3.323-2.422 2.056 2.056 0 0 1-3.323 2.422Z"
      }
    )
  ] });
};
const EmptyStateNoResultIllustration = () => {
  return /* @__PURE__ */ jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 176 175", fill: "none", children: [
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "#9DB9F2",
        fillRule: "evenodd",
        d: "M29.06 42.206c-3.565.97-5.676 4.625-4.713 8.164l22.486 82.683c.963 3.539 4.634 5.621 8.2 4.652l24.995-6.798a36.206 36.206 0 0 1-4.953-18.336c0-20.089 16.285-36.374 36.374-36.374.358 0 .715.005 1.07.015l-8.435-31.018-27.635-15.876L29.06 42.206Z",
        clipRule: "evenodd"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "#D9E5FF",
        fillRule: "evenodd",
        d: "M39.71 22.212a6.691 6.691 0 0 0-6.69 6.691v95.588a6.691 6.691 0 0 0 6.69 6.691h45.464c-5.3-5.716-8.546-13.406-8.546-21.863 0-17.649 14.137-31.956 31.576-31.956 2.756 0 5.429.357 7.976 1.028V47.065L92.283 22.212H39.71Z",
        clipRule: "evenodd"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "#fff",
        fillRule: "evenodd",
        d: "M40.665 38.46a2.868 2.868 0 0 1 2.868-2.867H74.12a2.868 2.868 0 1 1 0 5.735H43.533a2.868 2.868 0 0 1-2.868-2.867Zm0 76.472a2.868 2.868 0 0 1 2.868-2.868h33.18c.142 1.966.46 3.882.938 5.735H43.533a2.868 2.868 0 0 1-2.868-2.867Zm52.342-33.364a2.875 2.875 0 0 0-.725-.093h-48.75a2.868 2.868 0 0 0 0 5.736h42.196a32.467 32.467 0 0 1 7.279-5.643Zm-13.69 15.201H43.533a2.868 2.868 0 0 0 0 5.736H77.43a32.094 32.094 0 0 1 1.886-5.736ZM43.533 50.887a2.868 2.868 0 0 0 0 5.735h37.279a2.868 2.868 0 1 0 0-5.735h-37.28ZM40.665 69.05a2.868 2.868 0 0 1 2.868-2.868h62.132a2.868 2.868 0 0 1 0 5.736H43.533a2.868 2.868 0 0 1-2.868-2.868Z",
        clipRule: "evenodd"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "#D9E5FF",
        d: "M132.932 108.938c0 13.657-11.071 24.728-24.728 24.728-13.657 0-24.728-11.071-24.728-24.728 0-13.657 11.071-24.728 24.728-24.728 13.657 0 24.728 11.071 24.728 24.728Z"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "#9DB9F2",
        fillRule: "evenodd",
        d: "M108.585 131.054c12.424 0 22.496-10.072 22.496-22.496 0-12.425-10.072-22.497-22.496-22.497-12.425 0-22.497 10.072-22.497 22.497 0 12.424 10.072 22.496 22.497 22.496Zm0 4.895c15.127 0 27.391-12.263 27.391-27.391 0-15.128-12.264-27.392-27.391-27.392-15.128 0-27.392 12.264-27.392 27.392s12.264 27.391 27.392 27.391Z",
        clipRule: "evenodd"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "#9DB9F2",
        fillRule: "evenodd",
        d: "m147.783 148.68-21.397-20.326 3.95-4.158 21.397 20.326a2.869 2.869 0 0 1-3.95 4.158Z",
        clipRule: "evenodd"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "#fff",
        fillRule: "evenodd",
        d: "M119.037 100.35c-3.464-4.175-8.178-5.27-13.24-4.185a2.868 2.868 0 1 1-1.201-5.608c6.705-1.436 13.76-.01 18.855 6.132a2.868 2.868 0 1 1-4.414 3.661Z",
        clipRule: "evenodd"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "#9DB9F2",
        d: "m92.282 22.212 23.897 24.853h-16.25a7.647 7.647 0 0 1-7.647-7.647V22.212ZM122.773 77.409a4.895 4.895 0 1 1 6.923-6.924 4.895 4.895 0 0 1-6.923 6.924Z"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "#D9E5FF",
        d: "M90.106 142.403a4.895 4.895 0 1 1-6.923 6.923 4.895 4.895 0 0 1 6.922-6.923Z"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "#9DB9F2",
        d: "M133.341 82.131a2.448 2.448 0 1 1 3.461-3.461 2.448 2.448 0 0 1-3.461 3.461Z"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "#D9E5FF",
        d: "M79.538 137.68a2.448 2.448 0 1 1-3.463 3.462 2.448 2.448 0 0 1 3.463-3.462Z"
      }
    )
  ] });
};
const EmptyStateToAddIllustration = () => {
  return /* @__PURE__ */ jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 175 175", fill: "none", children: [
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "#fff",
        d: "m32.734 67.452 24.31-35.48h60.33l22.788 29.363 4.476 10.603-4.476 6.933-30.113-1.223L95.4 89.067H73.426l-10.173-11.42-22.788 1.224-7.731-11.419Z"
      }
    ),
    /* @__PURE__ */ jsx(
      "circle",
      {
        cx: "128.223",
        cy: "25.178",
        r: "5.423",
        fill: "#D9E5FF",
        transform: "rotate(45 128.223 25.178)"
      }
    ),
    /* @__PURE__ */ jsx(
      "circle",
      {
        cx: "133.972",
        cy: "36.132",
        r: "2.276",
        fill: "#D9E5FF",
        transform: "rotate(45 133.972 36.132)"
      }
    ),
    /* @__PURE__ */ jsx(
      "circle",
      {
        cx: "43.726",
        cy: "135.368",
        r: "4.552",
        fill: "#9DB9F2",
        transform: "rotate(45 43.726 135.368)"
      }
    ),
    /* @__PURE__ */ jsx(
      "circle",
      {
        cx: "49.726",
        cy: "127.152",
        r: "2.276",
        fill: "#9DB9F2",
        transform: "rotate(45 49.726 127.152)"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "#D9E5FF",
        fillRule: "evenodd",
        d: "M148.719 101.051c0 10.684-5.446 16.079-16.287 16.079h-14.406c-4.069-12.744-16.007-21.974-30.101-21.974-14.093 0-26.031 9.229-30.1 21.972H42.652c-10.788 0-16.287-5.342-16.287-16.079V72.42c0-4.67.78-6.844 2.959-9.852l17.942-24.223c5.654-7.727 8.768-9.75 17.947-9.75h44.655c9.184 0 12.295 2.023 17.947 9.75l17.947 24.223c2.177 3.008 2.956 5.185 2.956 9.854v28.629Zm-73.96-27.075c0 6.38 4.979 13.123 12.757 13.123 7.832 0 12.813-6.743 12.813-13.123v-.155c0-2.335 1.401-4.512 4.253-4.512h33.037c1.711 0 2.074-1.454 1.244-2.594l-19.138-26.347c-2.438-3.32-5.499-4.514-9.283-4.514H64.643c-3.786 0-6.846 1.192-9.283 4.512l-19.19 26.35c-.83 1.139-.467 2.59 1.245 2.59h33.09c2.853 0 4.254 2.18 4.254 4.515v.155Z",
        clipRule: "evenodd"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "#9DB9F2",
        d: "M115.227 127.136c0 15.135-12.269 27.405-27.405 27.405-15.135 0-27.405-12.27-27.405-27.405 0-15.136 12.27-27.405 27.405-27.405 15.136 0 27.405 12.269 27.405 27.405Z"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "#fff",
        fillRule: "evenodd",
        d: "M90.032 116.085a2.21 2.21 0 1 0-4.42 0v8.84h-8.84a2.21 2.21 0 1 0 0 4.42h8.84v8.84a2.21 2.21 0 1 0 4.42 0v-8.84h8.84a2.21 2.21 0 1 0 0-4.42h-8.84v-8.84Z",
        clipRule: "evenodd"
      }
    )
  ] });
};
const EmptyStateSuccessIllustration = () => {
  return /* @__PURE__ */ jsxs("svg", { viewBox: "0 0 176 175", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [
    /* @__PURE__ */ jsx(
      "rect",
      {
        x: "29.8042",
        y: "32.7173",
        width: "108.043",
        height: "32.7174",
        rx: "6.08696",
        fill: "#D9E5FF"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M16.8695 69.9995C13.5078 69.9995 10.7826 72.7247 10.7826 76.0865V96.6299C10.7826 99.9917 13.5078 102.717 16.8696 102.717H107.594C109.949 96.977 113.87 92.0453 118.826 88.4537V76.0865C118.826 72.7247 116.101 69.9995 112.739 69.9995H16.8695Z",
        fill: "#D9E5FF"
      }
    ),
    /* @__PURE__ */ jsx("circle", { cx: "25.9999", cy: "85.9781", r: "9.13043", fill: "#9DB9F2" }),
    /* @__PURE__ */ jsx(
      "rect",
      {
        x: "40.4564",
        y: "79.8911",
        width: "71.5217",
        height: "4.56522",
        rx: "2.28261",
        fill: "white"
      }
    ),
    /* @__PURE__ */ jsx(
      "rect",
      {
        x: "40.4564",
        y: "87.5",
        width: "44.1304",
        height: "4.56522",
        rx: "2.28261",
        fill: "white"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M132.635 83.8488C130.181 84.9831 127.272 83.9129 126.138 81.4586C125.003 79.0043 126.074 76.0952 128.528 74.9609C130.982 73.8267 133.891 74.8969 135.026 77.3512C136.16 79.8055 135.09 82.7146 132.635 83.8488Z",
        fill: "#9DB9F2"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M20.2344 65.6106C17.5355 65.4483 15.4791 63.1289 15.6414 60.4301C15.8036 57.7312 18.123 55.6749 20.8219 55.8371C23.5208 55.9993 25.5771 58.3187 25.4149 61.0176C25.2526 63.7165 22.9332 65.7728 20.2344 65.6106Z",
        fill: "#D9E5FF"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M140.68 75.693C139.453 76.2601 137.998 75.725 137.431 74.4979C136.864 73.2707 137.399 71.8161 138.626 71.249C139.853 70.6819 141.308 71.217 141.875 72.4441C142.442 73.6713 141.907 75.1259 140.68 75.693Z",
        fill: "#9DB9F2"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M13.2259 53.7291C11.8764 53.648 10.8483 52.4883 10.9294 51.1389C11.0105 49.7894 12.1702 48.7613 13.5196 48.8424C14.8691 48.9235 15.8973 50.0832 15.8161 51.4326C15.735 52.7821 14.5753 53.8102 13.2259 53.7291Z",
        fill: "#D9E5FF"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M106.101 107.282H35.8913C32.5295 107.282 29.8043 110.007 29.8043 113.369V133.913C29.8043 137.274 32.5295 140 35.8913 140H116.228C109.419 133.937 105.13 125.105 105.13 115.271C105.13 112.517 105.467 109.841 106.101 107.282Z",
        fill: "#D9E5FF"
      }
    ),
    /* @__PURE__ */ jsx("circle", { cx: "137.087", cy: "114.891", r: "27.3913", fill: "#9DB9F2" }),
    /* @__PURE__ */ jsx(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M152.583 105.14C153.418 106.084 153.33 107.527 152.386 108.362L133.17 125.369C132.245 126.188 130.836 126.122 129.992 125.22L123.032 117.79C122.17 116.87 122.217 115.425 123.138 114.563C124.058 113.701 125.502 113.749 126.364 114.669L131.807 120.48L149.361 104.944C150.305 104.108 151.747 104.196 152.583 105.14Z",
        fill: "white"
      }
    ),
    /* @__PURE__ */ jsx("circle", { cx: "45.0217", cy: "48.6954", r: "9.13043", fill: "#9DB9F2" }),
    /* @__PURE__ */ jsx(
      "rect",
      {
        x: "59.4781",
        y: "42.6084",
        width: "71.5217",
        height: "4.56522",
        rx: "2.28261",
        fill: "white"
      }
    ),
    /* @__PURE__ */ jsx(
      "rect",
      {
        x: "59.4781",
        y: "50.2173",
        width: "44.1304",
        height: "4.56522",
        rx: "2.28261",
        fill: "white"
      }
    ),
    /* @__PURE__ */ jsx("circle", { cx: "45.0217", cy: "123.261", r: "9.13043", fill: "#9DB9F2" }),
    /* @__PURE__ */ jsx(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M105.184 117.174H61.7608C60.5001 117.174 59.4781 118.196 59.4781 119.456C59.4781 120.717 60.5001 121.739 61.7608 121.739H105.762C105.467 120.251 105.272 118.727 105.184 117.174ZM59.4781 127.065C59.4781 125.804 60.5001 124.783 61.7608 124.783H101.326C102.587 124.783 103.609 125.804 103.609 127.065C103.609 128.326 102.587 129.348 101.326 129.348H61.7608C60.5001 129.348 59.4781 128.326 59.4781 127.065Z",
        fill: "white"
      }
    )
  ] });
};
const EmptyStateNotFoundIllustration = () => {
  return /* @__PURE__ */ jsxs("svg", { viewBox: "0 0 175 175", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [
    /* @__PURE__ */ jsx(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M28.1521 45.2715C24.7904 45.2715 22.0652 47.9967 22.0652 51.3585V121.358C22.0652 124.72 24.7904 127.445 28.1521 127.445H63.8386L84.0779 93.1059C86.9887 88.1673 94.0982 88.1673 97.009 93.1059L117.248 127.445H146.848C150.21 127.445 152.935 124.72 152.935 121.358V51.3584C152.935 47.9967 150.21 45.2715 146.848 45.2715H28.1521Z",
        fill: "#D9E5FF"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M88.2466 95.2829C89.286 93.5287 91.8248 93.5287 92.8642 95.2829L122.145 144.698C123.205 146.487 121.916 148.75 119.836 148.75H61.2747C59.1953 148.75 57.9059 146.487 58.9659 144.698L88.2466 95.2829Z",
        fill: "#9DB9F2"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M90.1631 133.806C89.5887 133.806 89.184 133.611 88.949 133.222C88.7402 132.832 88.6227 132.373 88.5966 131.845L87.8525 112.398C87.8264 111.48 87.9569 110.715 88.2441 110.103C88.5574 109.491 89.1971 109.185 90.1631 109.185C90.9985 109.185 91.586 109.449 91.9254 109.977C92.2648 110.478 92.4345 111.146 92.4345 111.981V112.398L91.6904 131.845C91.6643 132.373 91.5468 132.832 91.3379 133.222C91.1291 133.611 90.7375 133.806 90.1631 133.806ZM90.163 142.663C88.3877 142.663 87.5 141.703 87.5 139.783C87.5 138.977 87.7611 138.309 88.2832 137.78C88.8315 137.252 89.4712 136.987 90.2022 136.987C90.9071 136.987 91.5207 137.279 92.0428 137.864C92.565 138.42 92.8261 139.074 92.8261 139.825C92.8261 141.717 91.9384 142.663 90.163 142.663Z",
        fill: "white"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M22.0652 51.3589C22.0652 47.9972 24.7904 45.272 28.1521 45.272H146.848C150.21 45.272 152.935 47.9972 152.935 51.3589V58.9676H22.0652V51.3589Z",
        fill: "#9DB9F2"
      }
    ),
    /* @__PURE__ */ jsx(
      "rect",
      {
        x: "33.4783",
        y: "77.228",
        width: "106.522",
        height: "4.56522",
        rx: "2.28261",
        fill: "white"
      }
    ),
    /* @__PURE__ */ jsx(
      "rect",
      {
        x: "33.4783",
        y: "87.8804",
        width: "33.4783",
        height: "4.56522",
        rx: "2.28261",
        fill: "white"
      }
    ),
    /* @__PURE__ */ jsx(
      "rect",
      {
        x: "33.4783",
        y: "67.3369",
        width: "33.4783",
        height: "4.56522",
        rx: "2.28261",
        fill: "white"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M141.704 39.8772C139.249 41.0114 136.34 39.9413 135.206 37.4869C134.072 35.0326 135.142 32.1235 137.596 30.9893C140.051 29.855 142.96 30.9252 144.094 33.3795C145.228 35.8338 144.158 38.7429 141.704 39.8772Z",
        fill: "#9DB9F2"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M43.2751 131.732C45.6299 130.403 48.6159 131.235 49.9445 133.59C51.2731 135.945 50.4412 138.931 48.0865 140.259C45.7317 141.588 42.7457 140.756 41.4171 138.401C40.0885 136.047 40.9203 133.061 43.2751 131.732Z",
        fill: "#D9E5FF"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M149.748 31.7208C148.521 32.2879 147.067 31.7529 146.499 30.5257C145.932 29.2985 146.467 27.844 147.695 27.2769C148.922 26.7097 150.376 27.2448 150.943 28.472C151.51 29.6991 150.975 31.1537 149.748 31.7208Z",
        fill: "#9DB9F2"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M35.9149 140.511C37.0923 139.846 38.5853 140.262 39.2496 141.44C39.9139 142.617 39.498 144.11 38.3206 144.774C37.1432 145.439 35.6502 145.023 34.9859 143.845C34.3216 142.668 34.7375 141.175 35.9149 140.511Z",
        fill: "#D9E5FF"
      }
    )
  ] });
};
const ButtonStyledIconBadge = styled(IconBadge)`
  ${ICON_BADGE_ICON_SELECTOR} {
    /* Icon buttons should be exactly square */
    margin: 0 5px;
  }
`;
const IconBadgeButton = ({
  iconName,
  iconColor,
  statusType,
  ...buttonProps
}) => /* @__PURE__ */ jsx(Button, { buttonType: ButtonType.GHOST, ...omit(buttonProps, "buttonType"), children: /* @__PURE__ */ jsx(
  ButtonStyledIconBadge,
  {
    statusType,
    color: iconColor,
    iconName
  }
) });
export {
  BADGE_SELECTOR,
  Badge,
  BadgeDot,
  BadgeDotSize,
  BetaBadge,
  CHIPS_LIST_SELECTOR,
  Chip,
  ChipsList,
  ChipsSeparator,
  ClosableNotification,
  CustomBadge,
  DefaultNotification,
  EmptyState,
  EmptyStateActions,
  EmptyStateAddDashboardIllustration,
  EmptyStateNoResultIllustration,
  EmptyStateNotFoundIllustration,
  EmptyStateSuccessIllustration,
  EmptyStateToAddIllustration,
  ErrorNotification,
  FilterChip,
  FiltersBar,
  IconBadge,
  IconBadgeButton,
  InfoNotification,
  InteractiveNotification,
  LoadingWrapper,
  NOTIFICATION_SELECTOR,
  NewBadge,
  NewFeatureNotification,
  Notification,
  NotificationDetails,
  NotificationWrapper,
  ParseAPIErrorNotification,
  PreviewNotification,
  SaveNotification,
  StatusType,
  SuccessNotification,
  TAG_BUTTON_SELECTOR,
  TAG_DIV_SELECTOR,
  Tag,
  TagButton,
  TagGroup,
  ToastManager,
  ToastType,
  TrialBadge,
  WarningNotification,
  showToast
};
//# sourceMappingURL=index.js.map
